"use client";

import { IconEyeSlashOutline18, IconLockOutline18, IconShieldOutline18, IconUploadOutline18 } from "nucleo-ui-outline-18";
import { CheckBox } from "@/components/ui/CheckBox";
import ResponsiveModal from "@/components/custom/ResponsiveModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IframeStamper, KeyFormat } from "@turnkey/iframe-stamper";
import { trackWalletImportCompleted, trackWalletImportError } from "@/lib/analytics";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useCallback, useEffect, useRef, useState } from "react";
import { PrivateKeySecurityHero } from "./PrivateKeySecurityHero";
interface ImportPrivateKeyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  subOrgId: string;
  rootUserId: string;
  onSuccess: () => void;
}
type ImportStep = "name" | "import" | "success";

/**
 * Multi-step modal for importing an existing private key into a Turnkey-backed wallet.
 *
 * The raw key never reaches our servers — it is entered inside the Turnkey iframe
 * (import.turnkey.com), encrypted client-side, and only the encrypted bundle is
 * forwarded to our API to complete the import.
 */
export function ImportPrivateKeyModal({
  open,
  onOpenChange,
  organizationId,
  subOrgId,
  rootUserId,
  onSuccess
}: ImportPrivateKeyModalProps) {
  const [step, setStep] = useState<ImportStep>("name");
  const [walletName, setWalletName] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importedAddress, setImportedAddress] = useState<string | null>(null);
  const [iframeStamper, setIframeStamper] = useState<IframeStamper | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(() => typeof document !== "undefined" ? document.documentElement.classList.contains("dark") : false);
  const iframeContainerRef = useRef<HTMLDivElement>(null);
  const resetModalState = useCallback(() => {
    setStep("name");
    setWalletName("");
    setAcknowledged(false);
    setError(null);
    setImportedAddress(null);
    setIsLoading(false);
    setIframeStamper(null);
  }, []);
  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen) {
      resetModalState();
    }
    onOpenChange(nextOpen);
  }, [onOpenChange, resetModalState]);
  const applyIframeTheme = useCallback(async (stamper: IframeStamper) => {
    await stamper.applySettings({
      styles: {
        margin: "8px",
        padding: "0px",
        fontFamily: "ui-monospace, SFMono-Regular, monospace",
        fontSize: "12px",
        lineHeight: "18px",
        backgroundColor: "transparent",
        color: isDarkMode ? "#e4e4e7" : "#18181b",
        borderWidth: "0px",
        borderStyle: "none",
        borderColor: "transparent",
        borderRadius: "0px",
        maxWidth: "100%",
        height: "100%",
        maxHeight: "100%",
        resize: "none",
        overflowWrap: "anywhere",
        wordWrap: "break-word"
      }
    });
  }, [isDarkMode]);
  useEffect(() => {
    const root = document.documentElement;
    const syncTheme = () => {
      setIsDarkMode(root.classList.contains("dark"));
    };
    const observer = new MutationObserver(syncTheme);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ["class"]
    });
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!iframeStamper) return;
    void applyIframeTheme(iframeStamper);
  }, [iframeStamper, applyIframeTheme]);

  // Initialize iframe when entering import step
  useEffect(() => {
    if (step !== "import" || !iframeContainerRef.current) return;
    let stamper_0: IframeStamper | null = null;
    let cancelled = false;
    const iframeContainer = iframeContainerRef.current;
    const initIframe = async () => {
      try {
        setIsLoading(true);
        setError(null);
        stamper_0 = new IframeStamper({
          iframeUrl: "https://import.turnkey.com",
          iframeElementId: "turnkey-import-iframe",
          iframeContainer: iframeContainerRef.current,
          clearClipboardOnPaste: true
        });
        await stamper_0.init();

        // Size the iframe element itself
        const iframeEl = iframeContainerRef.current?.querySelector("#turnkey-import-iframe") as HTMLIFrameElement | null;
        if (iframeEl) {
          iframeEl.style.width = "100%";
          iframeEl.style.height = "100%";
          iframeEl.style.border = "none";
          iframeEl.style.display = "block";
          iframeEl.style.overflow = "hidden";
          iframeEl.setAttribute("scrolling", "no");
        }
        if (cancelled) return;
        await applyIframeTheme(stamper_0);
        if (cancelled) return;

        // Initialize the import flow by fetching an import bundle from our API
        const initResponse = await fetch("/api/turnkey/init-import", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            subOrgId,
            rootUserId,
            organizationId
          })
        });
        const initData = (await initResponse.json()) as {
          success: boolean;
          importBundle?: string;
          message?: string;
        };
        if (!initData.success) {
          throw new Error(initData.message || "Failed to initialize import");
        }
        if (!initData.importBundle) {
          throw new Error("No import bundle returned from server");
        }
        if (cancelled) return;

        // Inject the import bundle — this tells the iframe what sub-org to import into
        await stamper_0.injectImportBundle(initData.importBundle, subOrgId, rootUserId);
        if (!cancelled) {
          setIframeStamper(stamper_0);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to initialize import");
          setIsLoading(false);
        }
      }
    };
    initIframe();
    return () => {
      cancelled = true;
      if (iframeContainer) {
        const iframe = iframeContainer.querySelector("#turnkey-import-iframe");
        if (iframe) {
          iframe.remove();
        }
      }
    };
  }, [step, subOrgId, rootUserId, organizationId, applyIframeTheme]);
  const handleContinueToImport = () => {
    if (!walletName.trim() || !acknowledged) return;
    setStep("import");
  };
  const handleImport = useCallback(async () => {
    if (!iframeStamper) return;
    try {
      setIsLoading(true);
      setError(null);

      // Extract the encrypted bundle from the iframe — raw key stays in the browser
      const encryptedBundle = await iframeStamper.extractKeyEncryptedBundle(KeyFormat.Hexadecimal);
      const response = await fetch("/api/turnkey/import-private-key", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          subOrgId,
          rootUserId,
          encryptedBundle,
          walletName: walletName.trim(),
          organizationId
        })
      });
      const data = (await response.json()) as {
        success: boolean;
        address?: string;
        message?: string;
        wallet?: {
          name?: string;
        };
      };
      if (!data.success) {
        throw new Error(data.message || "Failed to import private key");
      }

      // Use the wallet name from the response in case the backend deduplicated it
      if (data.wallet?.name) setWalletName(data.wallet.name);
      setImportedAddress(data.address ?? null);
      setStep("success");
      trackWalletImportCompleted(data.address);
    } catch (err_0) {
      const rawMsg = err_0 instanceof Error ? err_0.message : "Failed to import private key";
      trackWalletImportError(rawMsg);
      if (rawMsg.includes("no target key found")) {
        setError("Please paste your private key into the input field above before importing.");
      } else {
        setError(rawMsg);
      }
    } finally {
      setIsLoading(false);
    }
  }, [iframeStamper, subOrgId, rootUserId, walletName, organizationId]);
  const handleDone = () => {
    onSuccess();
    onOpenChange(false);
  };
  return <ResponsiveModal open={open} onOpenChange={handleOpenChange} title={step === "name" ? "Import Private Key" : step === "import" ? "Enter Your Private Key" : "Import Successful"} contentClass="overflow-hidden p-0 sm:max-w-4xl">
      <div className="grid md:grid-cols-[minmax(0,420px)_minmax(0,1fr)] md:items-stretch">
        <PrivateKeySecurityHero icon={step === "success" ? IconShieldOutline18 : step === "import" ? IconUploadOutline18 : IconLockOutline18} eyebrow={step === "success" ? "Wallet Added" : "Secure Import"} title={step === "name" ? "Import Private Key" : step === "import" ? "Enter Your Private Key" : "Import Successful"} description={step === "name" ? "Securely import an existing EVM private key into your organization." : step === "import" ? "Paste your key into the secure input below. It is encrypted locally and never sent to our servers." : "Your wallet has been securely imported and is ready to use."} layout="vertical" maskRadius={320} logoLightUrl="https://cdn.prod.website-files.com/676848b756c25a3a99d7f262/691eebaa83af61f129213e45_Logo%20-%20Black.svg" logoDarkUrl="https://cdn.prod.website-files.com/676848b756c25a3a99d7f262/691eebb5414038ee68e4ec6d_Logo%20-%20White.svg" className="border-ds-neutral-200/80 dark:border-ds-neutral-800/80 min-h-[320px] rounded-none border-y-0 border-l-0 border-r md:min-h-[540px] md:rounded-l-2xl md:rounded-r-none" />

        <div className="space-y-5 p-6">
          {step === "name" && <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="import-wallet-name" className="text-ds-text-primary dark:text-zinc-100">
                  Wallet Name
                </Label>
                <Input id="import-wallet-name" placeholder="e.g., My Imported Wallet" value={walletName} onChange={e => setWalletName(e.target.value)} maxLength={100} />
              </div>

              <div className="border-ds-border-default dark:bg-ds-neutral-900/60 flex flex-col gap-3 rounded-xl border bg-zinc-50 p-4">
                <span className="label label-xs">How your key stays safe</span>

                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    <IconLockOutline18 className="text-ds-focus-brand h-6 w-6 shrink-0" />
                    <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs leading-relaxed">
                      <span className="text-ds-text-primary font-medium dark:text-zinc-200">
                        Client-side encryption
                      </span>{" "}
                      — your key is encrypted directly in your browser and never transmitted in plain text.
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <IconEyeSlashOutline18 className="text-ds-focus-brand h-6 w-6 shrink-0" />
                    <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs leading-relaxed">
                      <span className="text-ds-text-primary font-medium dark:text-zinc-200">
                        Zero-knowledge architecture
                      </span>{" "}
                      — our servers only receive an encrypted bundle. We never see or store your raw key.
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <IconShieldOutline18 className="text-ds-focus-brand h-6 w-6 shrink-0" />
                    <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs leading-relaxed">
                      <span className="text-ds-text-primary font-medium dark:text-zinc-200">HSM-grade protection</span>{" "}
                      — once imported, your key is stored in tamper-proof hardware security modules alongside your other
                      wallets.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-ds-focus-brand flex cursor-pointer rounded-xl border-2 border-dashed px-4 py-3">
                <CheckBox checked={acknowledged} onChange={checked => setAcknowledged(checked)} label="I understand my key will be securely encrypted and managed with my organization wallets." size="sm" />
              </div>

              {error && <p className="text-sm text-red-500">{error}</p>}

              <Button onClick={handleContinueToImport} disabled={!walletName.trim() || !acknowledged} className="w-full">
                <span>Continue</span>
              </Button>
            </div>}

          {step === "import" && <div className="space-y-4">
              <div className="space-y-2">
                {iframeStamper && <Label className="text-ds-text-primary dark:text-zinc-100">Private Key</Label>}
                <div ref={iframeContainerRef} className={iframeStamper ? "border-ds-border-default bg-ds-surface-card rounded-xl border" : ""} style={iframeStamper ? {
              height: "68px",
              overflow: "hidden"
            } : {
              height: "0px",
              overflow: "hidden",
              position: "absolute",
              opacity: 0
            }} />
                {iframeStamper && <div className="flex items-center gap-1.5">
                    <IconLockOutline18 className="h-3 w-3 text-blue-500" />
                    <span className="text-xs text-blue-600 dark:text-blue-400">
                      End-to-end encrypted — your key never leaves this browser
                    </span>
                  </div>}
              </div>

              {!iframeStamper && <div className="flex flex-col items-center justify-center gap-3 py-8">
                  <PixelLoader size="lg" color="blue" />
                  <p className="body-sm">Preparing secure environment...</p>
                  {error && <p className="text-sm text-red-500">{error}</p>}
                </div>}

              {iframeStamper && <>
                  {error && <p className="text-sm text-red-500">{error}</p>}

                  <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">
                    Paste your hex private key (with or without 0x prefix). Your clipboard is automatically cleared
                    after pasting for security.
                  </p>

                  <Button onClick={handleImport} disabled={isLoading} className="w-full">
                    {isLoading ? <>
                        <PixelLoader size="sm" className="mr-2" />
                        Encrypting & Importing...
                      </> : <>
                        <IconUploadOutline18 className="mr-2 h-4 w-4" />
                        Import Wallet
                      </>}
                  </Button>
                </>}
            </div>}

          {step === "success" && <div className="space-y-4">
              <div className="border-ds-border-default dark:bg-ds-neutral-900/60 space-y-2 rounded-xl border bg-zinc-50 px-3 py-3">
                <div>
                  <p className="meta-xs">Name</p>
                  <p className="text-ds-text-primary text-sm dark:text-zinc-200">{walletName}</p>
                </div>
                {importedAddress && <div>
                    <p className="meta-xs">Address</p>
                    <p className="text-ds-text-primary break-all font-mono text-sm dark:text-zinc-200">
                      {importedAddress}
                    </p>
                  </div>}
              </div>

              <Button onClick={handleDone} className="w-full">
                Done
              </Button>
            </div>}
        </div>
      </div>
    </ResponsiveModal>;
}